import React, { Fragment, useState, useMemo } from 'react'
import omit from 'lodash/omit'
import {
   Typography,
   Button,
   FormControl,
   FormControlLabel,
   InputLabel,
   Select,
   Checkbox,
   OutlinedInput,
   FormHelperText,
   MenuItem,
   TextField,
   Grid
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TooltipInputError from '../../toolTip/ToolTipInputError'
import { Autocomplete } from '@material-ui/lab'
import isEqual from 'lodash/isEqual'

import DebounceAutocomplete from '../../commons/debounceAutocomplete'
import DocumentNumberField from 'components/form/DocumentNumberField'

import Utils from 'utils/utils'
import TRANSLATE, { VALUES, REGEXP, OPERACIONES_USUARIO, INTERNAL_DOMAINS } from 'utils/constants'
import { useGetUserSettings } from 'utils/customHooks/useGetUserSettings'
import { useHasOperation } from 'hooks/useHasOperation'

const useStyles = makeStyles(theme => ({
   formContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: '0 auto 10px'
   },
   formControl: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: 14,
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   formControlTextArea: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: '14px',
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   button: {
      width: '100%',
      height: 40,
      marginTop: 20,
      color: 'white',
      background: '#3264DC'
   },
   formCleanButton: {
      color: '#3264DC',
      border: '1px solid #3264DC',
      fontWeight: 'bold',
      marginTop: 20,
      width: '100%'
   },
   helperText: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      fontSize: 11
   },
   selectEmpty: {
      marginTop: theme.spacing(2)
   },
   disabledButton: {
      backgroundColor: '#12B3F9!important',
      opacity: '0.3',
      color: '#fff!important'
   },
   MuiFormHelperText: {
      root: {
         margin: '0 !important'
      },
      margin: '0 !important'
   },
   chips: {
      display: 'flex',
      flexWrap: 'wrap'
   },
   chip: {
      margin: 2
   },
   multipleSelectLabel: {
      backgroundColor: 'white'
   },
   titleOne: {
      margin: '20px 0 15px 9px',
      fontStyle: 'normal',
      fontSize: 14,
      alignSelf: 'start',
      color: '#8A8FAB',
      fontWeight: 400
   },
   isAdminCheck: {
      justifyContent: 'space-between',
      marginLeft: '8px'
   },
   rolesOptions: {
      paddingLeft: 15,
      fontSize: 10,
      fontStyle: 'italic'
   }
}))

// TODO: improve this constants
const NO_MORE_THAN_15_CHARACTERS = Utils.noMoreThanNCharacters(15)
const NO_MORE_THAN_280_CHARACTERS = Utils.noMoreThanNCharacters(280)
const NO_MORE_THAN_200_CHARACTERS = Utils.noMoreThanNCharacters(200)
const NO_MORE_THAN_100_CHARACTERS = Utils.noMoreThanNCharacters(100)
const NO_MORE_THAN_60_CHARACTERS = Utils.noMoreThanNCharacters(60)
const NO_MORE_THAN_10_CHARACTERS = Utils.noMoreThanNCharacters(10)
const NO_MORE_THAN_7_CHARACTERS = Utils.noMoreThanNCharacters(7)
const MINIMUM_3_CHARACTERS = Utils.minimumNCharacters(3)

const RESTRICTED_DOMAINS = (process.env.REACT_APP_RESTRICTED_EMAIL_DOMAINS || INTERNAL_DOMAINS.join(','))
   .split(',')
   .map(domain => domain.trim().toLowerCase())
   .map(domain => (domain.startsWith('@') ? domain : `@${domain}`))

const INITIAL_USER_FORM_VALIDATION = {
   documentType: false,
   documentNumber: false,
   name: false,
   lastName: false,
   email: false,
   status: false,
   cellphone: false,
   telephone: false,
   roles: false,
   groups: false,
   entityNit: false,
   entityName: false,
   thirdPartyCode: false
}

const validRequired = value => !value || value === ''

const validationsFields = {
   documentType: validRequired,
   documentNumber: validRequired,
   name: value =>
      validRequired(value) || value.length < 3 || value.length > 200 || !REGEXP.VALIDATION_NAME_WHIT_NUMBER.exec(value),
   lastName: value =>
      validRequired(value) || value.length < 3 || value.length > 200 || !REGEXP.VALIDATION_NAME.exec(value),
   email: value =>
      validRequired(value) ||
      value.length > 280 ||
      !REGEXP.EMAIL.exec(value) ||
      RESTRICTED_DOMAINS.some(domain => value.toLowerCase().endsWith(domain)),
   status: validRequired,
   cellphone: value => validRequired(value) || value.length !== 10 || !REGEXP.CELLPHONE.exec(value),
   telephone: value => (!value ? false : (value && value.length > 7) || !REGEXP.TELEPHONE.exec(value)),
   groups: (value, formData) => value?.length === 0 && !formData?.roles?.length,
   roles: (value, formData) => value?.length === 0 && !formData?.groups?.length,
   entityNit: value => validRequired(value) || value.length > 200,
   entityName: value => validRequired(value) || value.length > 200,
   thirdPartyCode: value => value?.length == 0 || value == null
}

const UserForm = ({
   formData,
   setForm,
   actionSubmit,
   formBehavior,
   cleanCallback,
   availableRoles,
   groups,
   thirdTypes,
   documentTypes,
   fetchEntitiesByName,
   testDocNumberValidation
}) => {
   const { isInternalSuperAdmin, isInternalAdmin, userEntity } = useGetUserSettings()
   const [invalid, setInvalid] = useState(INITIAL_USER_FORM_VALIDATION)

   const classes = useStyles()

   const REGISTRAR_USUARIO = useHasOperation(OPERACIONES_USUARIO.REGISTRAR)
   const REGISTRAR_USUARIO_ADM = useHasOperation(OPERACIONES_USUARIO.REGISTRAR_ADM)
   const CONSULTAR_USUARIO = useHasOperation(OPERACIONES_USUARIO.CONSULTAR)

   const isSearch = useMemo(() => formBehavior === VALUES.FORM_BEHAVIOR.SEARCH, [formBehavior])
   const isEdit = useMemo(() => formBehavior === VALUES.FORM_BEHAVIOR.EDIT, [formBehavior])

   const newAvailablesRoles = useMemo(() => {
      if (isInternalSuperAdmin) return availableRoles
      return availableRoles.filter(e => e.name !== 'SEGURIDAD USUARIOS')
   }, [availableRoles, isInternalAdmin])

   function isInternalAdminAndInternalUser() {
      return isInternalAdmin && formData.userType === 'I'
   }

   function cleanForm() {
      cleanCallback()
      setInvalid(INITIAL_USER_FORM_VALIDATION)
   }

   function validateFields(name, value, formData) {
      validationsFields[name] = isEqual(name, 'documentNumber')
         ? value => testDocNumberValidation(formData?.documentType, value)
         : validationsFields[name]
      const isInvalidFn = validationsFields?.[name] ? validationsFields[name] : () => false
      setInvalid(inv => ({ ...inv, [name]: isInvalidFn(value, formData) }))
   }

   function addValuesToMemory(e) {
      const { name, value } = e.target
      setForm(prevFormData => ({
         ...prevFormData,
         [name]: value
      }))
      if (!isSearch) {
         validateFields(name, value, formData)
      }
   }

   function registerUser(type) {
      if (type === VALUES.FORM_BEHAVIOR.EDIT && isInternalAdminAndInternalUser()) {
         actionSubmit(VALUES.FORM_BEHAVIOR.EDIT)
         return
      }
      let isInvalidField = false
      let formFields =
         type === VALUES.FORM_BEHAVIOR.REGISTER
            ? omit(INITIAL_USER_FORM_VALIDATION, 'status')
            : INITIAL_USER_FORM_VALIDATION
      if (formData.documentType !== '' && formData.documentType === 'NI') {
         formFields = omit(formFields, 'lastName')
      }

      const newValidations = {}
      for (const key in formFields) {
         const isInvalid = validationsFields[key](formData[key], formData)
         newValidations[key] = isInvalid
         if (isInvalid && !isInvalidField) {
            isInvalidField = isInvalid
         }
      }
      if (isInvalidField) {
         setInvalid(prev => {
            return { ...prev, ...newValidations }
         })
      } else {
         actionSubmit(type)
      }
   }

   function actionSubmitEvent(e) {
      e.preventDefault()
      if (formBehavior === VALUES.FORM_BEHAVIOR.SEARCH) {
         actionSubmit(VALUES.FORM_BEHAVIOR.SEARCH)
      } else {
         registerUser(formBehavior)
      }
   }

   return (
      <div className={classes.formContainer}>
         <Typography className={classes.titleOne}>
            <b> Completa los campos </b>
         </Typography>

         <form className={classes.formContainer}>
            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               error={invalid.documentType}
               disabled={isInternalAdminAndInternalUser()}
            >
               <InputLabel htmlFor="outlined-adornment-amount">
                  {`Tipo de Identificación ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <Select
                  value={formData.documentType}
                  onChange={evt => {
                     addValuesToMemory(evt)
                     setForm(prev => ({
                        ...prev,
                        documentNumber: ''
                     }))
                  }}
                  size="small"
                  name="documentType"
                  label="Tipo de Identificación "
               >
                  {documentTypes.map(option => (
                     <MenuItem key={option.value} value={option.value}>
                        {option.label}
                     </MenuItem>
                  ))}
               </Select>
               <FormHelperText className={classes.helperText}>
                  {invalid.documentType && 'Ingrese un Tipo de Identificación válido'}
               </FormHelperText>
            </FormControl>

            <DocumentNumberField
               style={{ marginBottom: -10 }}
               formValue={formData.documentNumber}
               handleChange={addValuesToMemory}
               documentType={formData.documentType}
               fieldInvalid={invalid.documentNumber}
               disabled={isInternalAdminAndInternalUser() || !formData.documentType.length}
            />

            <FormControl variant="outlined" size="small" className={classes.formControl} error={invalid.name}>
               <InputLabel htmlFor="outlined-adornment-amount">
                  {formData.documentType === 'NI'
                     ? `Razón Social ${!isSearch ? '*' : ''}`
                     : `Nombres ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <OutlinedInput
                  error={invalid.name}
                  value={formData.name}
                  onChange={addValuesToMemory}
                  label={formData.documentType === 'NI' ? 'Razón Social ' : 'Nombres '}
                  name="name"
                  size="small"
                  inputProps={{ maxLength: 201 }}
                  endAdornment={
                     formData?.name?.length > 0 && formData?.name?.length < 3 && !isSearch ? (
                        <TooltipInputError title={MINIMUM_3_CHARACTERS} />
                     ) : (
                        formData?.name?.length === 201 && <TooltipInputError title={NO_MORE_THAN_200_CHARACTERS} />
                     )
                  }
                  disabled={isInternalAdminAndInternalUser()}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.name &&
                     (formData.documentType === 'NI'
                        ? 'Por favor ingrese una razón social válida'
                        : 'Por favor ingrese un nombre válido')}
               </FormHelperText>
            </FormControl>

            {formData.documentType !== 'NI' && (
               <FormControl variant="outlined" size="small" className={classes.formControl} error={invalid.lastName}>
                  <InputLabel htmlFor="outlined-adornment-amount">{`Apellidos ${!isSearch ? '*' : ''}`}</InputLabel>
                  <OutlinedInput
                     error={invalid.lastName}
                     value={formData.lastName}
                     onChange={addValuesToMemory}
                     label="Apellidos "
                     name="lastName"
                     size="small"
                     inputProps={{ maxLength: 201 }}
                     endAdornment={
                        formData?.lastName?.length > 0 && formData?.lastName?.length < 3 && !isSearch ? (
                           <TooltipInputError title={MINIMUM_3_CHARACTERS} />
                        ) : (
                           formData?.lastName?.length === 201 && (
                              <TooltipInputError title={NO_MORE_THAN_200_CHARACTERS} />
                           )
                        )
                     }
                     disabled={isInternalAdminAndInternalUser()}
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.lastName && 'Por favor ingrese un apellido válido'}
                  </FormHelperText>
               </FormControl>
            )}
            {/* formBehavior !== VALUES.FORM_BEHAVIOR.EDIT  */}
            {!isEdit || (isEdit && formData?.userType === 'E') ? (
               <FormControl variant="outlined" size="small" className={classes.formControl} error={invalid.email}>
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Correo Electrónico ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.email}
                     value={formData.email}
                     onChange={addValuesToMemory}
                     label="Correo Electrónico "
                     name="email"
                     size="small"
                     inputProps={{ maxLength: 281 }}
                     endAdornment={
                        formData?.email?.length === 281 && <TooltipInputError title={NO_MORE_THAN_280_CHARACTERS} />
                     }
                     disabled={isInternalAdminAndInternalUser()}
                  />

                  <FormHelperText className={classes.helperText}>
                     {invalid.email && 'Ingrese un correo electrónico válido'}
                  </FormHelperText>
               </FormControl>
            ) : (
               <div />
            )}
            {formBehavior !== VALUES.FORM_BEHAVIOR.REGISTER && (
               <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  size="small"
                  error={invalid.status}
                  disabled={isInternalAdminAndInternalUser()}
               >
                  <InputLabel htmlFor="outlined-type-native-simple">{`Estado ${!isSearch ? '*' : ''}`}</InputLabel>

                  <Select
                     value={formData.status}
                     onChange={addValuesToMemory}
                     size="small"
                     name="status"
                     label="Estado "
                     disabled={isEdit}
                  >
                     <MenuItem aria-label="None" value={''} />
                     {VALUES.STATUS_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                           {option.label}
                        </MenuItem>
                     ))}
                  </Select>
                  <FormHelperText className={classes.helperText}>
                     {invalid.status && 'El campo Estado es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            {formBehavior === VALUES.FORM_BEHAVIOR.SEARCH &&
               (!isInternalAdmin && !isInternalSuperAdmin ? (
                  <FormControl
                     variant="outlined"
                     className={classes.formControl}
                     size="small"
                     error={invalid.entityName}
                  >
                     <InputLabel htmlFor="outlined-adornment-amount">Entidad *</InputLabel>
                     <OutlinedInput value={userEntity || ''} label="Entidad " name="entityName" size="small" disabled />
                  </FormControl>
               ) : (
                  <FormControl
                     variant="outlined"
                     size="small"
                     className={classes.formControl}
                     error={invalid.entityName}
                  >
                     <DebounceAutocomplete
                        getOptionsAsync={fetchEntitiesByName}
                        textFieldLabel={'Entidad *'}
                        def={formData?.entity}
                        textFieldError={invalid.entityName}
                        renderOption={option => <span>{`${option.name} / ${option.nit}`}</span>}
                        getOptionLabel={option => (option?.name ? option.name + ' / ' + option.nit : '')}
                        onChange={(e, newValue, reason) => {
                           const isClear = reason === 'clear' || newValue === null
                           setForm(prev => ({
                              ...prev,
                              entityNit: isClear ? '' : newValue.nit,
                              entityName: isClear ? '' : newValue.name,
                              entity: isClear ? null : newValue,
                              userEntity: isClear ? null : newValue
                           }))
                        }}
                        disabled={isInternalAdminAndInternalUser()}
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.entityName && 'Por favor seleccione una entidad válida'}
                     </FormHelperText>
                  </FormControl>
               ))}

            {formBehavior === VALUES.FORM_BEHAVIOR.SEARCH && isInternalAdmin && (
               <FormControl variant="outlined" className={classes.formControl} size="small">
                  <FormControlLabel
                     control={
                        <Checkbox
                           color="primary"
                           checked={formData.adminUser}
                           onChange={event => {
                              setForm(old => ({
                                 ...old,
                                 adminUser: event.target.checked
                              }))
                           }}
                        />
                     }
                     label="Es administrador?"
                     labelPlacement="start"
                     className={classes.isAdminCheck}
                  />
               </FormControl>
            )}

            {!isSearch && (
               <Fragment>
                  <FormControl
                     variant="outlined"
                     className={classes.formControl}
                     size="small"
                     error={invalid.cellphone}
                     disabled={isInternalAdminAndInternalUser()}
                  >
                     <InputLabel htmlFor="outlined-adornment-amount">Número de Celular *</InputLabel>
                     <OutlinedInput
                        error={invalid.cellphone}
                        value={formData.cellphone}
                        onChange={e => {
                           const parsedInt = e.target.value.replace(/[^0-9]/g, '')
                           addValuesToMemory({
                              target: {
                                 name: 'cellphone',
                                 value: parsedInt
                              }
                           })
                        }}
                        label="Número de Celular "
                        name="cellphone"
                        size="small"
                        inputProps={{ maxLength: 11 }}
                        endAdornment={
                           formData?.cellphone?.length === 11 && (
                              <TooltipInputError title={NO_MORE_THAN_10_CHARACTERS} />
                           )
                        }
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.cellphone && 'Ingrese un celular válido'}
                     </FormHelperText>
                  </FormControl>

                  <FormControl
                     variant="outlined"
                     className={classes.formControl}
                     size="small"
                     error={invalid.telephone}
                  >
                     <InputLabel htmlFor="outlined-adornment-amount">Teléfono</InputLabel>
                     <OutlinedInput
                        error={invalid.telephone}
                        value={formData.telephone}
                        onChange={e => {
                           const parsedInt = e.target.value.replace(/[^0-9]/g, '')
                           addValuesToMemory({
                              target: {
                                 name: 'telephone',
                                 value: parsedInt
                              }
                           })
                        }}
                        label="Teléfono "
                        name="telephone"
                        size="small"
                        inputProps={{ maxLength: 8 }}
                        endAdornment={
                           formData?.telephone?.length === 8 && <TooltipInputError title={NO_MORE_THAN_7_CHARACTERS} />
                        }
                        disabled={isInternalAdminAndInternalUser()}
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.telephone && 'Ingrese un teléfono válido'}
                     </FormHelperText>
                  </FormControl>
                  <FormControl
                     variant="outlined"
                     className={classes.formControl}
                     size="small"
                     error={invalid.thirdPartyCode}
                  >
                     <Autocomplete
                        // multiple
                        size="small"
                        options={thirdTypes.filter(option => option?.description !== 'APORTANTE')}
                        name="thirdPartyCode"
                        getOptionLabel={option => `${option?.description || ''}`}
                        renderOption={option => `${option?.description}`}
                        value={formData.thirdPartyCode}
                        disabled={isEdit}
                        onChange={(e, newValue) => {
                           addValuesToMemory({
                              target: {
                                 name: 'thirdPartyCode',
                                 value: newValue
                              }
                           })
                        }}
                        renderInput={params => (
                           <TextField
                              {...params}
                              label={'Tipo de Tercero'}
                              variant="outlined"
                              error={invalid.thirdPartyCode}
                           />
                        )}
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.thirdPartyCode && 'Seleccione al menos un tipo de tercero'}
                     </FormHelperText>
                  </FormControl>

                  <FormControl variant="outlined" className={classes.formControl} size="small" error={invalid.roles}>
                     <Autocomplete
                        multiple
                        size="small"
                        name="roles"
                        options={newAvailablesRoles}
                        getOptionLabel={option => option.name}
                        renderOption={option => <span key={option.id}>{option.name}</span>}
                        value={formData.roles}
                        onChange={(e, newValue) => {
                           addValuesToMemory({
                              target: {
                                 name: 'roles',
                                 value: newValue
                              }
                           })
                        }}
                        renderInput={params => (
                           <TextField
                              {...params}
                              label={'Nombre de(los) Roles(s) *'}
                              variant="outlined"
                              value={''}
                              error={invalid.roles}
                           />
                        )}
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.roles && 'Seleccione al menos un rol válido'}
                     </FormHelperText>
                  </FormControl>
                  <FormControl variant="outlined" className={classes.formControl} size="small" error={invalid.groups}>
                     <Autocomplete
                        multiple
                        size="small"
                        name="groups"
                        options={groups}
                        getOptionLabel={option => option?.name}
                        renderOption={option => (
                           <Grid container direction="column" key={option.id}>
                              <div>{option.name}</div>
                              <Grid container direction="column">
                                 {option?.roles?.map(rol => (
                                    <Typography
                                       variant="body2"
                                       className={classes.rolesOptions}
                                       key={`${option?.id}-${rol?.id}`}
                                    >
                                       {rol?.name}
                                    </Typography>
                                 ))}
                              </Grid>
                           </Grid>
                        )}
                        value={formData.groups}
                        onChange={(e, newValue) => {
                           addValuesToMemory({
                              target: {
                                 name: 'groups',
                                 value: newValue
                              }
                           })
                        }}
                        renderInput={params => (
                           <TextField
                              {...params}
                              label={'Nombre de(los) Grupo(s) *'}
                              variant="outlined"
                              value={''}
                              error={invalid.groups}
                           />
                        )}
                     />
                     <FormHelperText className={classes.helperText}>
                        {invalid.groups && 'Seleccione al menos un grupo'}
                     </FormHelperText>
                  </FormControl>

                  {!isInternalAdmin && !isInternalSuperAdmin ? (
                     <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        size="small"
                        error={invalid.entityName}
                     >
                        <InputLabel htmlFor="outlined-adornment-amount">Entidad *</InputLabel>
                        <OutlinedInput
                           value={formData.userEntity || formData.entityName || ''}
                           label="Entidad"
                           name="entityName"
                           size="small"
                           disabled
                        />
                     </FormControl>
                  ) : (
                     <Fragment>
                        <FormControl
                           variant="outlined"
                           size="small"
                           className={classes.formControl}
                           error={invalid.entityName}
                        >
                           <DebounceAutocomplete
                              getOptionsAsync={fetchEntitiesByName}
                              textFieldLabel={'Entidad *'}
                              def={formData?.entity || (formData?.userEntity?.name ? formData.userEntity : null)}
                              textFieldError={invalid.entityName}
                              renderOption={option => <span>{`${option.name} / ${option.nit}`}</span>}
                              getOptionLabel={option => (option?.name ? option.name + ' / ' + option.nit : '')}
                              onChange={(e, newValue, reason) => {
                                 const isClear = reason === 'clear' || newValue === null
                                 setForm(prev => ({
                                    ...prev,
                                    entityNit: isClear ? '' : newValue.nit,
                                    entityName: isClear ? '' : newValue.name,
                                    entity: isClear ? null : newValue,
                                    userEntity: isClear ? null : newValue
                                 }))
                              }}
                              disabled={isInternalAdminAndInternalUser()}
                           />
                           <FormHelperText className={classes.helperText}>
                              {invalid.entityName && 'Por favor seleccione una entidad válida'}
                           </FormHelperText>
                        </FormControl>

                        {isInternalSuperAdmin && (
                           <FormControl variant="outlined" className={classes.formControl} size="small">
                              <FormControlLabel
                                 control={
                                    <Checkbox
                                       color="primary"
                                       checked={formData.adminUser}
                                       disabled={!REGISTRAR_USUARIO_ADM}
                                       onChange={event => {
                                          setForm(old => ({
                                             ...old,
                                             adminUser: event.target.checked
                                          }))
                                       }}
                                    />
                                 }
                                 label="Es administrador?"
                                 labelPlacement="start"
                                 className={classes.isAdminCheck}
                              />
                           </FormControl>
                        )}
                     </Fragment>
                  )}
               </Fragment>
            )}

            <Button
               type="submit"
               fullWidth
               variant="contained"
               color="primary"
               className={classes.button}
               onClick={actionSubmitEvent}
               disabled={isSearch ? !CONSULTAR_USUARIO : !REGISTRAR_USUARIO}
            >
               {isEdit ? TRANSLATE.BUTTONS.EDIT : isSearch ? TRANSLATE.BUTTONS.SEARCH : TRANSLATE.BUTTONS.SAVE}
            </Button>

            <Button
               classes={{
                  root: classes.formCleanButton
               }}
               variant="outlined"
               onClick={cleanForm}
            >
               {TRANSLATE.BUTTONS.CLEAN}
            </Button>
         </form>
      </div>
   )
}

export default UserForm
